/**
 * Bransje og tariffavtale (#294 ONB-08): the words for the rule profile. The sector suggests what to
 * check and decides no rule; the working-time rules stay the hardcoded Norwegian ones (ADR 0010). The
 * status comes from the server (services/ruleProfile.js), so Kom i gang and this page say the same.
 */
import { T } from '@/i18n';
import type { RuleProfile, RuleSector } from '@/shell/tenant';

export const SECTORS: { key: RuleSector; label: string; check: string }[] = [
  {
    key: 'hospitality',
    label: 'Overnatting og servering',
    check: 'Se særlig på kvelds- og helgearbeid, pauser og om en tariffavtale gjelder de ansatte.',
  },
  {
    key: 'retail',
    label: 'Handel',
    check: 'Avklar åpningstider, helgearbeid, stillingsprosenter og om en tariffavtale gjelder.',
  },
  {
    key: 'security',
    label: 'Vakt og sikkerhet',
    check: 'Avklar turnus, nattarbeid, lange vakter og om en tariffavtale gjelder.',
  },
  {
    key: 'cleaning',
    label: 'Renhold',
    check: 'Avklar arbeidssteder og om allmenngjorte vilkår eller en tariffavtale gjelder.',
  },
  {
    key: 'construction',
    label: 'Bygg og anlegg',
    check: 'Avklar arbeidssteder og hvilke vilkår som gjelder for de enkelte ansatte.',
  },
  {
    key: 'office',
    label: 'Kontor og tjenester',
    check: 'Avklar avtalt uke, fleksitid og hvordan faktisk arbeid registreres.',
  },
  {
    key: 'health',
    label: 'Helse og omsorg',
    check: 'Turnus og særskilte avtaler må avklares. Timefly har ingen egne regler for helse og omsorg.',
  },
  { key: 'other', label: 'Annet eller flere bransjer', check: 'Avklar grupper og avtaler hver for seg.' },
];

/**
 * The sector the register's næringskode points at, as a suggestion the administrator still chooses;
 * null when the code says nothing we offer. Two-digit NACE divisions, 81.2 for cleaning.
 */
export function suggestedSector(nace: string | null | undefined): RuleSector | null {
  const c = (nace ?? '').trim();
  if (!c) return null;
  const div = c.slice(0, 2);
  if (div === '55' || div === '56') return 'hospitality';
  if (div === '47') return 'retail';
  if (div === '80') return 'security';
  if (c.startsWith('81.2')) return 'cleaning';
  if (div === '41' || div === '42' || div === '43') return 'construction';
  if (div === '86' || div === '87' || div === '88') return 'health';
  return null;
}

/** The status in the words Innstillinger and Kom i gang both use. */
export const profileLabel = (s: RuleProfile['status']) =>
  s === 'confirmed' ? T('Gjennomgått') : s === 'review' ? T('Må avklares') : T('Ikke gjennomgått');

const MISSING: Record<RuleProfile['missing'][number], string> = {
  sector: 'bransje',
  agreement: 'om dere har tariffavtale',
  agreement_name: 'navnet på avtalen',
  effective_from: 'gjelder fra',
};
/** «bransje og gjelder fra»: what is left before the profile can be confirmed. */
export function missingText(m: RuleProfile['missing']): string {
  const words = m.map((k) => T(MISSING[k]));
  return words.length < 2 ? (words[0] ?? '') : `${words.slice(0, -1).join(', ')} ${T('og')} ${words.at(-1)}`;
}
